'use strict'
/**
 * sessionTypeTemplate.js — Contrato de coherencia costura periodización ↔ template.
 *
 * CONTEXTO (MINI-MACRO F3-FIX · OLA 2):
 * `session_type` viene de la periodización (sesion.tipo); el template lo elige
 * `selectWorkoutTemplate` con contexto completo (goal + level + location + weekPhase).
 * Cuando el plan de periodización es incoherente con el perfil (ej. P12 endurance runner
 * recibe longevity_gym_3dias → sesion.tipo='fuerza_full_body'), las dos capas divergen.
 *
 * REGLA DE VERDAD: el template manda.
 * - selectWorkoutTemplate tiene todo el contexto → su workout_type es la fuente canónica.
 * - sesion.tipo describe la intención del plan de gym; si ese plan no es correcto para el
 *   usuario, su label es ruido.
 * - Bug 1 (extendido) ya hace la reconciliación en el orchestrator.
 * - Este contrato es el centinela: lo detecta en observación y lo loguea, no throw.
 *
 * MODO: observación (log, no throw). Equivalente a A.2 de la doctrina contratos.
 * SEAM: se llama DESPUÉS del Bug 1 fix, cuando sessionType y workout_template_code
 *       son ambos definitivos.
 *
 * PRODUCTOR: src/engine/orchestrator.js (buildEnrichedDayPlan, línea ~2675)
 */

const { z }                = require('./z')
const { validateContract } = require('./validate')

// Familias lógicas de session_type
const SESSION_FAMILIES = {
  strength: ['fuerza', 'fuerza_full_body', 'fuerza_tren_inferior', 'fuerza_upper', 'fuerza_tren_superior'],
  cardio:   ['cardio', 'zona2', 'cardio_hiit', 'marathon_race'],
  recovery: ['active_recovery', 'rest', 'mobility'],
}

// workout_type del template → familia esperada de session_type
const TEMPLATE_TYPE_FAMILY = {
  strength:        'strength',
  cardio:          'cardio',
  hiit:            'cardio',    // hiit es cardio de alta intensidad
  active_recovery: 'recovery',
  mobility:        'recovery',
}

function classifySession(sessionType) {
  for (const [fam, types] of Object.entries(SESSION_FAMILIES)) {
    if (types.includes(sessionType)) return fam
  }
  return null  // desconocido → no opinar
}

const SeamCoherenceSchema = z
  .object({
    sessionType:         z.string(),
    templateCode:        z.string(),
    templateWorkoutType: z.string(),
  })
  .refine(
    (d) => {
      const sessionFam  = classifySession(d.sessionType)
      const tplFam      = TEMPLATE_TYPE_FAMILY[d.templateWorkoutType] ?? null
      if (!sessionFam || !tplFam) return true  // familias desconocidas → no opinar
      // Incoherencias duras:
      //   session_type strength + template cardio/recovery → roto
      //   session_type cardio   + template strength        → roto
      if (sessionFam === 'strength' && tplFam !== 'strength') return false
      if (sessionFam === 'cardio'   && tplFam === 'strength') return false
      return true
    },
    (d) => ({
      message: `session_type '${d.sessionType}' (familia ${classifySession(d.sessionType) ?? '?'}) `
             + `incoherente con template '${d.templateCode}' (workout_type '${d.templateWorkoutType}', `
             + `familia ${TEMPLATE_TYPE_FAMILY[d.templateWorkoutType] ?? '?'})`,
    }),
  )

/**
 * Valida la coherencia session_type ↔ template en modo observación.
 * Nunca lanza — solo loguea si el contrato se rompe.
 *
 * @param {{ sessionType: string, templateCode: string, templateWorkoutType: string }} params
 */
function validateSessionTypeTemplateCohesion({ sessionType, templateCode, templateWorkoutType }) {
  validateContract(
    SeamCoherenceSchema,
    { sessionType, templateCode, templateWorkoutType },
    { contract: 'SeamSessionTypeTemplate', source: 'orchestrator/buildEnrichedDayPlan' },
  )
}

module.exports = {
  SeamCoherenceSchema,
  validateSessionTypeTemplateCohesion,
  SESSION_FAMILIES,
  TEMPLATE_TYPE_FAMILY,
}
